import React, { useEffect, useState } from 'react';
import gql from 'graphql-tag';

import { useParams } from 'react-router';
import { API } from 'aws-amplify';
import { graphqlOperation } from '@aws-amplify/api';
import { Avatar, Button, Flex, Form, Input } from 'antd';
import { LoadingOutlined, UserOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';

import { GiideRouteParams } from '../../types';
import { IState, Subscription } from '../../graphql/types';

import { setAIConversation, updateAIConverstion } from '../../actions';
import {
  AiChatHistory,
  AiStartChatRequestOutput,
  GetAiChatHistoryQuery,
} from '../../graphql/api';
import Logo from '../Logo';
import { colors } from '../../theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import styles from './FeedPlayer.module.css';
import TextArea from 'antd/es/input/TextArea';
import { BouncingBalls } from '../Spinners/BouncingBalls';

const startAiChatRequestMutation = gql`
  mutation aiStartChatRequest($giideId: ID!, $input: String!, $script: String) {
    aiStartChatRequest(
      input: { giideId: $giideId, input: $input, script: $script }
    ) {
      id
      success
      error
    }
  }
`;

const onGetAiChatResponse = gql`
  subscription onGetAiChatResponse($giideId: ID!) {
    onGetAiChatResponse(giideId: $giideId) {
      script
    }
  }
`;

const getAiChatHistory = gql`
  query getAiChatHistory($giideId: ID!) {
    getAiChatHistory(giideId: $giideId) {
      chatHistory {
        type
        content
      }
    }
  }
`;

/*const promptSuggestions = [
  {
    name: 'Make it shorter',
    value: 'Can you make this giide 25% shorter?',
  },
  {
    name: 'More casual',
    value: 'Can you make the tone of this giide more casual?',
  },
  {
    name: 'Try again',
    value: 'Can you try again from scratch, and make a giide about this topic?',
  },
];*/

type AIMessageProps = {
  content: string;
  isLoading: boolean;
  isLast: boolean;
  error?: boolean;
};

const AIMessage = ({ content, isLoading, isLast, error }: AIMessageProps) => {
  return (
    <Flex gap={'small'} className={'ai-message'}>
      <Avatar
        className="chat-avatar"
        shape="circle"
        icon={
          <div className={'chat-ai-logo-wrap'}>
            <Logo white />
          </div>
        }
      />

      {isLast && isLoading ? (
        <BouncingBalls />
      ) : isLast ? (
        <>
          <div>{content}</div>
          <Form.Item name="script">
            <Input type="hidden" value={content} />
          </Form.Item>
        </>
      ) : (
        <div className={error ? 'ai-message-error' : ''}>{content}</div>
      )}
    </Flex>
  );
};

const UserMessage = ({ content, avatar }) => {
  return (
    <Flex gap={'small'} className="user-message">
      <Avatar
        className={'user-chat-avatar'}
        shape="circle"
        icon={<UserOutlined />}
        src={avatar}
      />
      <div>{content}</div>
    </Flex>
  );
};

const AI: React.FC = () => {
  const { id: giideId } = useParams<GiideRouteParams>();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = React.useState(false);
  const [lastInput, setLastInput] = React.useState<string | false>(false);

  const conversationHist = useSelector<IState, AiChatHistory[]>(
    state => state.AI[giideId]?.conversationHistory || []
  );

  const [form] = Form.useForm();

  const scrollableDivRef = React.useRef<HTMLDivElement>(null);

  const avatar = useSelector<IState, string | undefined>(state => state.avatar);

  useEffect(() => {
    if (isLoading) {
      if (scrollableDivRef.current) {
        scrollableDivRef.current.scrollTop =
          scrollableDivRef.current.scrollHeight;
      }
    }
  }, [isLoading]);

  const submitMessage = React.useCallback(
    async values => {
      dispatch(updateAIConverstion(giideId, 'user', values.input));

      setIsLoading(true);
      setLastInput(values.input);

      try {
        await (API.graphql(
          graphqlOperation(startAiChatRequestMutation, {
            giideId,
            ...values,
          })
        ) as Promise<AiStartChatRequestOutput>);
      } catch (error) {
        console.log('error updating script: ', error);
      }
    },
    [dispatch /*form*/, giideId, setIsLoading]
  );

  useEffect(() => {
    const observable = API.graphql(
      graphqlOperation(onGetAiChatResponse, {
        giideId: giideId,
      })
    );

    let subscription: Subscription | null = null;

    if ('subscribe' in observable) {
      subscription = observable.subscribe({
        next: (next: { value: { data: any } }) => {
          const script = next?.value?.data?.onGetAiChatResponse?.script;

          if (script) {
            dispatch(
              updateAIConverstion(
                giideId,
                'ai',
                next.value.data.onGetAiChatResponse.script
              )
            );

            form.setFieldsValue({ script: script });
            form.resetFields(['input']); // Reset only the input field
            setIsLoading(false);
          }
        },
        error: () => null,
      });
    }

    return () => subscription?.unsubscribe();
  }, [dispatch, giideId, form]);

  // const showPromptSuggestions = !isLoading && !!lastInput;

  // const handleSubmitSuggestion: MouseEventHandler<
  //   HTMLButtonElement
  // > = event => {
  //   const buttonElem = event.currentTarget as HTMLButtonElement;
  //   form.setFieldsValue({ input: buttonElem.value });
  //   form.submit();
  // };

  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    const fetchChatHistory = async () => {
      try {
        const result = (await API.graphql({
          query: getAiChatHistory,
          variables: {
            giideId: giideId,
          },
        })) as { data: GetAiChatHistoryQuery };

        if (result.data?.getAiChatHistory?.chatHistory) {
          const filteredHistory: AiChatHistory[] =
            result.data.getAiChatHistory.chatHistory.filter(
              (item): item is AiChatHistory => item !== null
            );

          dispatch(setAIConversation(giideId, filteredHistory));

          const lastAiResponse = filteredHistory.findLast(x => x.type === 'ai');
          if (lastAiResponse) {
            form.setFieldsValue({ script: lastAiResponse.content });
          }
        }
      } catch (error) {
        console.log(error);
      } finally {
        setInitialLoading(false);
      }
    };

    if (giideId) {
      fetchChatHistory();
    }
  }, [dispatch, giideId, form]);

  return (
    <div className={'notes-area'}>
      <div className={'notes-box'}>
        <p>
          What do you want to share today? This space is available to jot down
          any notes you might have before you record your audio.
        </p>
        <TextArea />
        <div className={'drag-tip'}>Drag to resize</div>
      </div>
      <Form
        className="ai-assist-form"
        form={form}
        name="ai-assist"
        layout="vertical"
        onFinish={submitMessage}
      >
        {!initialLoading && conversationHist.length > 0 && (
          <div
            ref={scrollableDivRef}
            className="ai-chat-history"
            style={{
              height: 'fit-content',
              position: 'relative',
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
            }}
          >
            <div>
              {conversationHist.map((message, i) => {
                const isLast = conversationHist.length - 1 === i;
                const key = `${i}-${message.type}`;

                if (message.type === 'user') {
                  return (
                    <UserMessage
                      key={key}
                      content={message.content}
                      avatar={avatar}
                    />
                  );
                }

                if (message.type === 'ai') {
                  return (
                    <AIMessage
                      key={key}
                      content={message.content}
                      isLoading={isLoading}
                      isLast={isLast}
                    />
                  );
                }

                return null;
              })}

              {isLoading && (
                <AIMessage
                  key={'ai-loader'}
                  content=""
                  isLoading={true}
                  isLast={true}
                />
              )}
            </div>
          </div>
        )}

        {/*<div className="ai-prompt-section">*/}
        {/*  /!*<Expander isOpen={showPromptSuggestions}>*!/*/}
        {/*  /!*  <div className="prompt-suggestions">*!/*/}
        {/*  /!*    {promptSuggestions.map(suggestion => (*!/*/}
        {/*  /!*      <button*!/*/}
        {/*  /!*        className="ai-suggestion-button"*!/*/}
        {/*  /!*        type="button"*!/*/}
        {/*  /!*        onClick={handleSubmitSuggestion}*!/*/}
        {/*  /!*        disabled={isLoading}*!/*/}
        {/*  /!*        name="input"*!/*/}
        {/*  /!*        value={suggestion.value}*!/*/}
        {/*  /!*      >*!/*/}
        {/*  /!*        {suggestion.name}*!/*/}
        {/*  /!*      </button>*!/*/}
        {/*  /!*    ))}*!/*/}
        {/*  /!*  </div>*!/*/}
        {/*  /!*</Expander>*!/*/}
        {/*</div>*/}
        <div className={'ai-input-section'}>
          {initialLoading && (
            <Flex
              gap={'small'}
              justify={'center'}
              className={styles.scriptLoader}
            >
              <LoadingOutlined spin />
              <div>Loading...</div>
            </Flex>
          )}

          {!initialLoading && conversationHist.length === 0 && (
            <div className={'ai-instructions-text'}>
              <p>
                Not sure where to begin? Use our AI prompt to help get your
                topic started.
              </p>
            </div>
          )}

          {!initialLoading && (
            <Form.Item
              name="input"
              rules={[
                { required: true, message: 'Please input your instructions' },
              ]}
            >
              <Input
                placeholder={
                  !lastInput
                    ? 'Today I want to talk about...'
                    : 'Send additional instructions'
                }
                className="ai-input"
                autoComplete="off"
                suffix={
                  <Button
                    type="text"
                    htmlType="submit"
                    disabled={isLoading}
                    className={'ai-form-submit'}
                    icon={
                      <FontAwesomeIcon color={colors.black3} icon={faArrowUp} />
                    }
                  />
                }
                disabled={isLoading}
              />
            </Form.Item>
          )}
        </div>
      </Form>
    </div>
  );
};

export default AI;
